import React, { useState, useEffect } from 'react';

// Material UI
import { useTheme } from '@mui/material/styles';
import {
    styled,
    Box,
    Grid,
    Button,
    CircularProgress,
    useMediaQuery,
    Typography,
} from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import LaunchIcon from '@mui/icons-material/Launch';

// Components
import { LoadingPage, ErrorPage } from 'components/ui/states';
import ReceiptTable from 'components/tables/ReceiptTable';
import OrderDetails from 'components/orders/OrderDetails';
import OrderProducts from 'components/orders/OrderProducts';
import OrderSeats from 'components/orders/OrderSeats';
import TicketEmailer from 'components/orders/TicketEmailer';
import TicketDownloader from 'components/orders/TicketDownloader';
import RegistrationAnswers from 'components/orders/RegistrationAnswers';
import ActivityLog from 'components/orders/ActivityLog';
import Comments from 'components/orders/Comments';
import ConfirmAction from 'components/popups/ConfirmAction';
import { ActionButton } from 'components/ui/buttons';

// Utility
import { useAuth } from 'context/auth';
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { useNotification } from 'context/notification';
import { formatPrice, parseDecimalToPercent } from 'utility/numbers';

// TODO: Refactor this component to render the FullScreenModal Component instead of relying on the parent to wrap it in the modal.
//       See OrderCreateModal for an example

export default function OrderDetailModal(props) {
    // Props
    const { tranId, eventId, onUpdate } = props;

    const { notify } = useNotification();

    // We have to use the media query hook to determine where to render the comments component.
    // sx prop with display: none is not enough. It will render the component in 2 places, and they will maintain separate state.
    const mobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    // *** Order Detail State *** //

    const [state, setState] = useState({
        status: 'loading', // loading, error, success
        order: null,
        products: null,
        attendees: [],
        unipasses: [],
        payouts: null,
    });

    const [refresh, setRefresh] = useState(0); // Increment this to refresh the order history

    //  *** Refund Order *** //

    const [confirmRefund, setConfirmRefund] = useState(false);
    const [loadingRefund, setLoadingRefund] = useState(false);

    function refundOrder() {
        fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}/refund`, {
            method: 'PUT',
            body: { eventId: eventId },
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Order has been refunded');
                    setRefresh((r) => r + 1);
                    if (onUpdate) {
                        onUpdate(); // Sometimes the page rendering the order list does not need to be refreshed
                    }
                } else {
                    notify.error('There was a problem with the refund');
                    setLoadingRefund(false);
                }
            })
            .catch(() => {
                notify.error('There was a problem with the refund');
                setLoadingRefund(false);
            });

        // Close confirmation dialog and set loading state
        setConfirmRefund(false);
        setLoadingRefund(true);
    }

    useEffect(() => {
        (async () => {
            try {
                const [o, p, a] = await Promise.all([
                    fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}/`),
                    fetchExpresso(`/apiv2/events/${eventId}/payouts?tz=${moment.tz.guess()}`),
                    fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}/activity`),
                ]);

                if (o.status !== 200 || p.status !== 200 || a.status !== 200) {
                    throw new Error();
                }

                const [orderDetails, payouts, activity] = await Promise.all([
                    o.json(),
                    p.json(),
                    a.json(),
                ]);

                setState((s) => ({
                    ...s,
                    status: 'success',
                    order: orderDetails.order,
                    products: orderDetails.products,
                    activity: activity,
                    attendees: orderDetails.attendees,
                    unipasses: orderDetails.unipasses,
                    payouts: payouts,
                }));
            } catch (e) {
                setState((s) => ({ ...s, status: 'error' }));
            }
        })();
    }, [tranId, eventId, refresh]);

    const { auth } = useAuth();
    const theme = useTheme();

    if (state.status === 'loading') {
        return <LoadingPage />;
    }

    if (state.status === 'error') {
        return <ErrorPage message="Unable to find order" />;
    }

    const statusFinancial = state.order.status_financial;
    const orderApproved = Boolean(
        statusFinancial && statusFinancial >= 1000 && statusFinancial <= 1999,
    ); // A status_financial of 0 can result in this expression resulting in 0 instead of a boolean. We have to explicitly convert the result to a boolean.
    const questionsEnabled = state.attendees.length > 0;
    const seatsEnabled = orderApproved && state.order.seats_flag === 1;
    const enableRefund =
        statusFinancial &&
        statusFinancial >= 1000 &&
        statusFinancial <= 1999 &&
        state.payouts?.some((p) => p.final_payout_flag === 1) === false; // We allow 'refunds' on free orders for now since it allows us to void those tickets
    const orderRefunded = state.order.status_financial === 3000;
    const multiTicket = state.unipasses.length > 1;

    // Host Absorbs (Total)
    let hostTotal = 0;

    const {
        host_absorbs_b4t_fees,
        host_absorbs_cc_fees,
        connect_type_id,
        cc_fee_order_amt,
        cc_fee_order_pct,
        our_fee_total,
        cc_fee_total,
        tax_on_products,
        sub_total,
        stripe_transaction_url_host,
        stripe_transaction_url_platform,
    } = state.order;

    let fees = 0;

    if (host_absorbs_b4t_fees) {
        fees = Number((fees + our_fee_total).toFixed(2));
    }

    if (host_absorbs_cc_fees) {
        fees = Number((fees + cc_fee_total).toFixed(2));
    }

    const subTotalCalc = Number((sub_total - state.order.disc_order_total).toFixed(2));

    hostTotal = Number((subTotalCalc + tax_on_products - fees).toFixed(2));

    return (
        <Root>
            <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                p={[2, 4]}
                maxWidth={theme.breakpoints.values.xl}
            >
                <Box display="flex">
                    <Box flexGrow={1}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={6}>
                                <OrderDetails admin={auth.admin} order={state.order} />
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Card>
                                    <CardHeader title="Payment Summary" />
                                    <CardContent>
                                        <ReceiptTable
                                            orderDetails={{ ...state.order, hostTotal: hostTotal }}
                                            products={state.products}
                                        />
                                    </CardContent>
                                    <CardActions>
                                        {enableRefund === true && (
                                            <RefundButton
                                                startIcon={
                                                    loadingRefund ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={14}
                                                        />
                                                    ) : (
                                                        <RotateLeftIcon />
                                                    )
                                                }
                                                onClick={
                                                    loadingRefund
                                                        ? undefined
                                                        : () => setConfirmRefund(true)
                                                }
                                            >
                                                {loadingRefund ? 'Refunding...' : 'Refund Order'}
                                            </RefundButton>
                                        )}
                                        {orderRefunded && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<RotateLeftIcon />}
                                                disabled
                                            >
                                                Order Refunded
                                            </Button>
                                        )}
                                        {auth.admin && stripe_transaction_url_platform && (
                                            <ActionButton
                                                href={stripe_transaction_url_platform}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<LaunchIcon />}
                                            >
                                                Open in Stripe
                                            </ActionButton>
                                        )}
                                        {!auth.admin && stripe_transaction_url_host && (
                                            <ActionButton
                                                href={stripe_transaction_url_host}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<LaunchIcon />}
                                            >
                                                Open in Stripe
                                            </ActionButton>
                                        )}
                                    </CardActions>
                                </Card>
                                {connect_type_id === 3 && (
                                    <Box m={1}>
                                        <Typography variant="caption">
                                            FrontDoor+ estimates Stripe fees at{' '}
                                            {parseDecimalToPercent(cc_fee_order_pct, 2)}% +{' '}
                                            {formatPrice(cc_fee_order_amt)} per online order. Actual
                                            Stripe charges may differ slightly depending on card
                                            type, currency conversion, or discounts provided by
                                            Stripe. If something does not look right, we are happy
                                            to help.
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <OrderProducts
                                    eventId={eventId}
                                    tranId={tranId}
                                    canEdit={orderApproved}
                                    onEdit={() => {
                                        setRefresh((current) => current + 1);
                                        onUpdate?.();
                                    }}
                                />
                            </Grid>

                            {seatsEnabled && (
                                <Grid item xs={12}>
                                    <OrderSeats eventId={eventId} tranId={tranId} />
                                </Grid>
                            )}

                            {orderApproved && (
                                <Grid item xs={12} lg={6}>
                                    <TicketEmailer
                                        multiTicket={multiTicket}
                                        email={state.order.email}
                                        tranId={tranId}
                                        eventId={eventId}
                                        onSend={() => setRefresh(refresh + 1)}
                                    />
                                </Grid>
                            )}

                            {orderApproved && (
                                <Grid item xs={12} lg={6}>
                                    <TicketDownloader
                                        eventId={eventId}
                                        multiTicket={multiTicket}
                                        lastName={state.order.last_name}
                                        tranId={tranId}
                                        email={state.order.email}
                                        onDownload={() => setRefresh(refresh + 1)}
                                    />
                                </Grid>
                            )}

                            {questionsEnabled && (
                                <Grid item xs={12} xl={6}>
                                    <RegistrationAnswers eventId={eventId} tranId={tranId} refreshToken={refresh} />
                                </Grid>
                            )}

                            {mobile === true && (
                                <>
                                    <Grid item xs={12}>
                                        <Comments tranId={tranId} eventId={eventId} />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{ display: { xs: 'block', md: 'none' } }}
                                    >
                                        <ActivityLog activity={state.activity} />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>

                    {mobile === false && (
                        <Box ml={3}>
                            <Comments tranId={tranId} eventId={eventId} />
                            <Box mb={4} />
                            <ActivityLog activity={state.activity} />
                        </Box>
                    )}
                </Box>
            </Box>
            <ConfirmAction
                title={'Refund Order'}
                description={
                    'The customer will be refunded their money, and their tickets will be voided. Are you sure you want to continue?'
                }
                destructive
                confirmText="Refund"
                open={confirmRefund}
                onConfirm={refundOrder}
                onCancel={() => setConfirmRefund(false)}
            />
        </Root>
    );
}

const Root = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}));

const RefundButton = styled(Button)(({ theme }) => ({
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
        borderColor: theme.palette.error.main,
    },
}));
RefundButton.defaultProps = { variant: 'outlined' };
